import React, { useState, useEffect, useMemo } from "react";
import {
    Box, Paper, Typography, TextField, Button, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Grid, Card, CardContent, Tooltip, IconButton, Alert, MenuItem, Select,
    FormControl, InputLabel
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_BASE } from "../../apiBase";

function useAuthHeaders() {
    const token = localStorage.getItem("token");
    return useMemo(() => ({
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
    }), [token]);
}

export default function AdminActivityLogsSubTab() {
    const headers = useAuthHeaders();

    // Filters
    const [adminId, setAdminId] = useState("");
    const [action, setAction] = useState("");
    const [resourceType, setResourceType] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [failedOnly, setFailedOnly] = useState(false);
    const [limit, setLimit] = useState(100);

    // Data
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Fetch logs
    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError("");

            const params = new URLSearchParams();
            if (adminId) params.append("admin_id", adminId);
            if (action) params.append("action", action);
            if (resourceType) params.append("resource_type", resourceType);
            if (failedOnly) params.append("failed_only", "true");
            if (startDate) params.append("start_date", new Date(startDate).toISOString());
            if (endDate) params.append("end_date", new Date(endDate).toISOString());
            params.append("limit", limit);

            const res = await fetch(`${API_BASE}/admin/admin-activities?${params.toString()}`, {
                headers
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch admin activity logs: ${res.status}`);
            }

            const data = await res.json();
            setLogs(data.activities || data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch summary
    const fetchSummary = async () => {
        try {
            setLoadingSummary(true);
            const res = await fetch(`${API_BASE}/admin/admin-activities/summary?days=30`, {
                headers
            });

            if (res.ok) {
                const data = await res.json();
                setSummary(data);
            }
        } catch (err) {
            console.error("Failed to fetch summary:", err);
        } finally {
            setLoadingSummary(false);
        }
    };

    // Load on mount
    useEffect(() => {
        fetchLogs();
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Admin Activity Logs (ISDS A2-4.1.6.e)
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Comprehensive tracking of all administrative operations (user management, config changes, etc.).
            </Typography>

            {/* Summary Cards */}
            {summary && (
                <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="primary">
                                    {summary.total_activities}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Activities (30 days)
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="error">
                                    {summary.failed_activities}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Failed Activities
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="success.main">
                                    {summary.success_rate}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Success Rate
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Top Admin
                                </Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {summary.activities_by_admin?.[0]?.admin || "—"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {summary.activities_by_admin?.[0]?.count || 0} actions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Admin ID"
                            type="number"
                            value={adminId}
                            onChange={(e) => setAdminId(e.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Action"
                            value={action}
                            onChange={(e) => setAction(e.target.value)}
                            placeholder="e.g., user_created"
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Resource Type"
                            value={resourceType}
                            onChange={(e) => setResourceType(e.target.value)}
                            placeholder="e.g., User, APIKey"
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Start Date"
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="End Date"
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                onClick={fetchLogs}
                                disabled={loading}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={20} /> : "Search"}
                            </Button>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchSummary} disabled={loadingSummary}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {/* Logs Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Admin</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Resource</TableCell>
                            <TableCell>Endpoint</TableCell>
                            <TableCell>IP Address</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No admin activities found. Adjust filters or wait for admin actions.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>
                                        <Tooltip title={log.created_at}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {formatTimestamp(log.created_at)}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.admin || log.admin_username}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.action}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.resource_type ? `${log.resource_type}${log.resource_id ? `:${log.resource_id}` : ''}` : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.endpoint || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.ip_address || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.success ? "Success" : "Failed"}
                                            size="small"
                                            color={log.success ? "success" : "error"}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {logs.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {logs.length} activities (limit: {limit})
                    </Typography>
                    {logs.length >= limit && (
                        <Typography variant="body2" color="warning.main">
                            Results limited. Increase limit or refine filters to see more.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
